/**
 * @fileoverview Sticky Add to Cart Component
 * Handles sticky ATC visibility, variant updates, and cart integration
 * 
 * 📡 EVENT SUMMARY:
 * 
 * EVENTS LISTENED:
 * - scroll → Controls visibility based on scroll position
 * - variant:change → Updates price, availability, and button state
 * - variant:loaded → Updates initial state from URL
 * - variant:unavailable → Handles unavailable variants
 * - resize → Recalculates trigger positions
 * 
 * EVENTS DISPATCHED:
 * - sticky-atc:shown → Emitted when sticky ATC becomes visible
 * - sticky-atc:hidden → Emitted when sticky ATC becomes hidden
 * - sticky-atc:closed → Emitted when user closes sticky ATC
 * 
 * EVENT DATA STRUCTURE:
 * - sticky-atc:shown: { element: HTMLElement, triggerPosition: number }
 * - sticky-atc:hidden: { element: HTMLElement, scrollPosition: number }
 * - sticky-atc:closed: { element: HTMLElement, closedByUser: boolean }
 */

export class StickyATC {
  constructor(element, options = {}) {
    if (!element) {
      throw new Error('StickyATC requires a valid element');
    }

    this.element = element;
    this.options = {
      // Visibility options
      triggerOffset: 200, // Show after scrolling 200px past trigger
      hideOnMobile: false, // Show on mobile by default
      autoHide: true, // Auto hide when scrolling back up
      
      // Animation options
      animationDuration: 300,
      animationEasing: 'ease-out',
      
      // Behavior options
      closeOnAddToCart: false, // Keep open after adding to cart
      rememberUserClose: true, // Remember if user closed it
      closeable: this.element.dataset.closeable !== 'false', // Can be closed by user (read from data attribute)
      
      // Integration options
      formatMoney: true, // Format prices using Shopify's money format
      updateImage: true, // Update product image on variant change
      
      ...options
    };

    // State
    this.isVisible = false;
    this.isClosedByUser = false;
    this.variants = [];
    this.currentVariant = null;
    this.triggerPosition = null;
    this.productForm = null;

    // Elements cache
    this.elements = {};

    // Bound methods
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleVariantLoaded = this.handleVariantLoaded.bind(this);
    this.handleVariantUnavailable = this.handleVariantUnavailable.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleStickyThumbnailClick = this.handleStickyThumbnailClick.bind(this);
    this.handleCartAdded = this.handleCartAdded.bind(this);
    this.handleCartError = this.handleCartError.bind(this);
    this.handleOperationStarted = this.handleOperationStarted.bind(this);
    this.handleOperationCompleted = this.handleOperationCompleted.bind(this);
    this.handleOperationFailed = this.handleOperationFailed.bind(this);

    this.init();
  }

  /**
   * Initialize the sticky ATC component
   */
  init() {
    console.log('🏷️ [StickyATC] Initializing sticky add to cart...');

    try {
      this.cacheElements();
      this.loadVariants();
      this.findTriggerPosition();
      this.bindEvents();
      this.checkInitialState();
      
      console.log('✅ [StickyATC] Initialized successfully', {
        variants: this.variants.length,
        triggerPosition: this.triggerPosition,
        productForm: !!this.productForm,
        closeable: this.options.closeable,
        isClosedByUser: this.isClosedByUser
      });
    } catch (error) {
      console.error('❌ [StickyATC] Failed to initialize:', error);
    }
  }

  /**
   * Cache DOM elements
   */
  cacheElements() {
    this.elements = {
      wrapper: this.element.querySelector('.sticky-atc__wrapper'),
      content: this.element.querySelector('.sticky-atc__content'),
      image: this.element.querySelector('.sticky-atc__product-image'),
      title: this.element.querySelector('.sticky-atc__title'),
      price: this.element.querySelector('.sticky-atc__price-display .sticky-atc__price') || this.element.querySelector('.sticky-atc__price'),
      comparePrice: this.element.querySelector('.sticky-atc__compare-price'),
      priceWrapper: this.element.querySelector('.sticky-atc__price-wrapper'),
      priceDisplay: this.element.querySelector('.sticky-atc__price-display'),
      savings: this.element.querySelector('[data-savings]'),
      variantTitle: this.element.querySelector('[data-variant-title]'),
      fullTitleElement: this.element.querySelector('[data-sticky-atc-full-title]'),
      variantImageWraps: this.element.querySelectorAll('.sticky-atc__thumbnail[data-variant-id]'),
      variantInputs: this.element.querySelectorAll('input[name="id"], [data-variant-input]'),
      button: this.element.querySelector('[data-add-to-cart]'),
      buttonText: this.element.querySelector('[data-button-text]'),
      buyNowButton: this.element.querySelector('[data-buy-now]'),
      loading: this.element.querySelector('[data-loading]'),
      closeButton: this.element.querySelector('[data-sticky-atc-close]')
    };

    // Find the main product form on the page
    this.productForm = document.querySelector('[data-product-form]');

    // Hide close button if component is not closeable
    if (!this.options.closeable && this.elements.closeButton) {
      this.elements.closeButton.style.display = 'none';
      console.log('🏷️ [StickyATC] Hid close button for non-closeable component');
    }

    console.log('🏷️ [StickyATC] Elements cached', {
      hasWrapper: !!this.elements.wrapper,
      hasButton: !!this.elements.button,
      hasPrice: !!this.elements.price,
      hasProductForm: !!this.productForm,
      hasCloseButton: !!this.elements.closeButton,
      closeButtonVisible: this.elements.closeButton ? this.elements.closeButton.style.display !== 'none' : false
    });
  }

  /**
   * Load variant data from JSON script
   */
  loadVariants() {
    const variantScript = this.element.querySelector('[data-product-variants]');
    if (!variantScript) {
      console.warn('🏷️ [StickyATC] No variant data found');
      return;
    }

    try {
      this.variants = JSON.parse(variantScript.textContent);
      console.log('🏷️ [StickyATC] Loaded variants:', this.variants.length);
    } catch (error) {
      console.error('🏷️ [StickyATC] Failed to parse variant data:', error);
      this.variants = [];
    }
  }

  /**
   * Find the trigger position (usually the main add to cart button)
   */
  findTriggerPosition() {
    // Look for the main product form or add to cart button
    const triggers = [
      this.productForm,
      document.querySelector('[data-add-to-cart]'),
      document.querySelector('.product-form'),
      document.querySelector('.product__add-to-cart')
    ].filter(Boolean);

    if (triggers.length === 0) {
      console.warn('🏷️ [StickyATC] No trigger element found, using default position');
      this.triggerPosition = 400;
      return;
    }

    const trigger = triggers[0];
    const rect = trigger.getBoundingClientRect();
    this.triggerPosition = window.scrollY + rect.bottom + this.options.triggerOffset;

    console.log('🏷️ [StickyATC] Trigger position calculated:', {
      element: trigger.tagName,
      position: this.triggerPosition,
      offset: this.options.triggerOffset
    });
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Scroll and resize events
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);

    // Variant change events
    document.addEventListener('variant:change', this.handleVariantChange);
    document.addEventListener('variant:loaded', this.handleVariantLoaded);
    document.addEventListener('variant:unavailable', this.handleVariantUnavailable);

    // Component events
    if (this.elements.closeButton) {
      this.elements.closeButton.addEventListener('click', this.handleClose);
    }

    // Variant thumbnail clicks select variant (sync with main form swatches)
    this.element.addEventListener('click', this.handleStickyThumbnailClick);

    // Cart integration - the centralized cart system will handle add to cart clicks
    // We just need to listen for cart events to update our UI
    if (this.elements.button) {
      // Add data-submit-button to make it work with centralized cart system
      this.elements.button.setAttribute('data-submit-button', '');
      
      // Listen for cart events to update loading states
      document.addEventListener('cart:added', this.handleCartAdded);
      document.addEventListener('cart:error', this.handleCartError);
      document.addEventListener('operation:started', this.handleOperationStarted);
      document.addEventListener('operation:completed', this.handleOperationCompleted);
      document.addEventListener('operation:failed', this.handleOperationFailed);
    }

    console.log('🏷️ [StickyATC] Event listeners bound');
  }

  /**
   * Check initial state and show/hide accordingly
   */
  checkInitialState() {
    const storageKey = `sticky-atc-closed-${this.element.dataset.productId}`;
    
    // Check if user previously closed it (only if closeable)
    if (this.options.closeable && this.options.rememberUserClose) {
      this.isClosedByUser = localStorage.getItem(storageKey) === 'true';
    } else {
      // If not closeable, ensure it's never considered closed by user
      this.isClosedByUser = false;
      
      // Clear any existing localStorage preference since it's no longer relevant
      if (localStorage.getItem(storageKey)) {
        localStorage.removeItem(storageKey);
        console.log('🏷️ [StickyATC] Cleared localStorage preference for non-closeable component');
      }
    }

    // Initialize savings/price for current selected variant
    try {
      const currentVariantId = this.elements.button?.getAttribute('data-variant-id');
      if (currentVariantId && this.variants && this.variants.length) {
        const initialVariant = this.variants.find(v => String(v.id) === String(currentVariantId));
        if (initialVariant) {
          this.updatePrice(initialVariant);
          this.updateVariantTitle(initialVariant);
        }
      }
    } catch (e) {
      console.warn('🏷️ [StickyATC] Failed to initialize price/savings on load', e);
    }

    // Check mobile visibility
    if (this.options.hideOnMobile && window.innerWidth < 768) {
      return;
    }

    // Check scroll position
    this.handleScroll();
  }

  /**
   * Handle scroll events
   */
  handleScroll() {
    // Only check user closed state if component is closeable
    if (this.options.closeable && this.isClosedByUser) return;
    if (this.options.hideOnMobile && window.innerWidth < 768) return;

    const shouldShow = window.scrollY > this.triggerPosition;

    if (shouldShow && !this.isVisible) {
      this.show();
    } else if (!shouldShow && this.isVisible && this.options.autoHide) {
      this.hide();
    }
  }

  /**
   * Handle resize events
   */
  handleResize() {
    // Recalculate trigger position
    this.findTriggerPosition();
    
    // Check mobile visibility
    if (this.options.hideOnMobile && window.innerWidth < 768 && this.isVisible) {
      this.hide();
    }
  }

  /**
   * Handle variant change events
   */
  handleVariantChange(event) {
    const { variantId, variant, isAvailable } = event.detail;
    
    console.log('🏷️ [StickyATC] Variant changed:', {
      variantId,
      isAvailable,
      variant: {
        id: variant.id,
        title: variant.title,
        price: variant.price,
        compareAtPrice: variant.compare_at_price
      }
    });

    this.updateVariant(variant, isAvailable);
  }

  /**
   * Handle variant loaded events
   */
  handleVariantLoaded(event) {
    const { variantId, variant, isAvailable } = event.detail;
    
    console.log('🏷️ [StickyATC] Variant loaded:', { variantId, isAvailable });
    this.updateVariant(variant, isAvailable);
  }

  /**
   * Handle variant unavailable events
   */
  handleVariantUnavailable(event) {
    const { variantId, variant } = event.detail;
    
    console.log('🏷️ [StickyATC] Variant unavailable:', { variantId });
    this.updateVariant(variant, false);
  }

  /**
   * Update variant information in the sticky ATC
   */
  updateVariant(variant, isAvailable) {
    // Prefer our own variant JSON so compare_at_price and featured_image are always correct
    const ourVariant = this.variants.find((v) => String(v.id) === String(variant?.id));
    this.currentVariant = ourVariant ? { ...variant, ...ourVariant, compare_at_price: ourVariant.compare_at_price ?? variant?.compare_at_price ?? variant?.compareAtPrice } : variant;

    // Sync variant id to all inputs inside this sticky (so form submits correct variant)
    if (this.elements.variantInputs && this.elements.variantInputs.length) {
      this.elements.variantInputs.forEach((input) => {
        input.value = this.currentVariant.id;
      });
    }

    // Update price
    this.updatePrice(this.currentVariant);

    // Update variant title
    this.updateVariantTitle(this.currentVariant);

    // Update button state
    this.updateButton(this.currentVariant, isAvailable);

    // Update image if enabled
    if (this.options.updateImage) {
      this.updateImage(this.currentVariant);
    }

    // Variant thumbnail selection state
    this.updateVariantImageSelection(this.currentVariant);
  }

  /**
   * Update price display
   */
  updatePrice(variant) {
    if (!this.elements.price || !variant) return;

    // Update main price
    this.elements.price.textContent = this.formatMoney(variant.price);
    this.elements.price.setAttribute('data-price', variant.price);

    // Update compare at price (support both compare_at_price from JSON and compareAtPrice from VariantManager)
    if (this.elements.comparePrice) {
      const compareAt = variant.compare_at_price ?? variant.compareAtPrice;
      const priceNum = parseInt(variant.price || 0, 10);
      const compareNum = parseInt(compareAt || 0, 10);
      if (compareNum > priceNum) {
        this.elements.comparePrice.textContent = this.formatMoney(compareAt);
        this.elements.comparePrice.setAttribute('data-compare-price', compareAt);
        this.elements.comparePrice.style.display = 'inline';
      } else {
        this.elements.comparePrice.style.display = 'none';
      }
    }

    // Update savings display (supports string cents)
    if (this.elements.savings) {
      const price = parseInt(variant.price || 0, 10);
      const compareAt = parseInt(variant.compare_at_price || variant.compareAtPrice || 0, 10);
      if (compareAt > price) {
        const savings = compareAt - price;
        this.elements.savings.textContent = `You Save ${this.formatMoney(savings)}`;
        this.elements.savings.style.display = '';
      } else {
        this.elements.savings.style.display = 'none';
      }
    }

    console.log('🏷️ [StickyATC] Price updated:', {
      price: variant.price,
      comparePrice: variant.compare_at_price,
      formatted: this.formatMoney(variant.price)
    });
  }

  /**
   * Update variant title
   */
  updateVariantTitle(variant) {
    if (!variant) return;
    // Product title only (from data-product-title)
    if (this.elements.fullTitleElement) {
      const productTitle = this.elements.fullTitleElement.dataset.productTitle || '';
      this.elements.fullTitleElement.textContent = productTitle;
      return;
    }
    if (!this.elements.variantTitle) return;
    this.elements.variantTitle.textContent = variant.title;
  }

  /**
   * Update button state
   */
  updateButton(variant, isAvailable) {
    if (!this.elements.button || !variant) return;

    // Update data attributes
    this.elements.button.setAttribute('data-variant-id', variant.id);
    
    // Update button state
    if (isAvailable) {
      this.elements.button.disabled = false;
      this.elements.button.classList.remove('is-disabled');
      if (this.elements.buttonText) {
        this.elements.buttonText.textContent = 'Add to Cart';
      }
    } else {
      this.elements.button.disabled = true;
      this.elements.button.classList.add('is-disabled');
      if (this.elements.buttonText) {
        this.elements.buttonText.textContent = 'Sold Out';
      }
    }

    console.log('🏷️ [StickyATC] Button updated:', {
      variantId: variant.id,
      isAvailable,
      disabled: this.elements.button.disabled
    });
  }

  /**
   * Update product image
   */
  updateImage(variant) {
    if (!this.elements.image || !variant || !variant.featured_image) return;

    this.elements.image.src = variant.featured_image;
    this.elements.image.alt = variant.title;

    console.log('🏷️ [StickyATC] Image updated:', variant.featured_image);
  }

  /**
   * Variant thumbnail click — select that variant (trigger main form swatch so variant:change fires)
   */
  handleStickyThumbnailClick(event) {
    const wrap = event.target.closest('.sticky-atc__thumbnail[data-variant-id]');
    if (!wrap || wrap.classList.contains('is-selected')) return;
    const variantId = wrap.dataset.variantId;
    if (!variantId) return;
    const swatch = document.querySelector(`.variant-swatch[data-variant-id="${variantId}"]`);
    if (swatch) {
      swatch.click();
    } else {
      const variant = this.variants.find((v) => String(v.id) === String(variantId));
      if (variant) {
        this.updateVariant(variant, variant.available);
        const mainInput = document.querySelector('[data-product-form] input[name="id"], [data-product-form] [data-variant-id]');
        if (mainInput) {
          mainInput.value = variantId;
        }
        document.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variantId, variant, isAvailable: variant.available }
        }));
      }
    }
  }

  /**
   * Update selected state on variant thumbnails
   */
  updateVariantImageSelection(variant) {
    if (!this.elements.variantImageWraps || !this.elements.variantImageWraps.length || !variant) return;
    const id = String(variant.id);
    this.elements.variantImageWraps.forEach((wrap) => {
      if (String(wrap.dataset.variantId) === id) {
        wrap.classList.add('is-selected');
        const img = wrap.querySelector('.sticky-atc__product-image');
        if (img && variant.featured_image) {
          img.src = variant.featured_image;
          img.alt = variant.title;
        }
      } else {
        wrap.classList.remove('is-selected');
      }
    });
  }

  /**
   * Handle close button click
   */
  handleClose() {
    // Only allow closing if component is closeable
    if (!this.options.closeable) {
      console.warn('🏷️ [StickyATC] Close attempted but component is not closeable');
      return;
    }

    this.isClosedByUser = true;
    this.hide();

    // Remember user preference
    if (this.options.rememberUserClose) {
      const storageKey = `sticky-atc-closed-${this.element.dataset.productId}`;
      localStorage.setItem(storageKey, 'true');
    }

    this.dispatchEvent('sticky-atc:closed', {
      element: this.element,
      closedByUser: true
    });

    console.log('🏷️ [StickyATC] Closed by user');
  }

  /**
   * Handle cart added event
   */
  handleCartAdded(event) {
    console.log('🏷️ [StickyATC] Cart item added');
    this.setLoading(false);
    if (this.options.closeOnAddToCart) {
      setTimeout(() => this.hide(), 1000);
    }
  }

  /**
   * Handle cart error event
   */
  handleCartError(event) {
    console.log('🏷️ [StickyATC] Cart error occurred');
    this.setLoading(false);
  }

  /**
   * Handle cart operation started event
   */
  handleOperationStarted(event) {
    if (event.detail.type === 'addToCart') {
      console.log('🏷️ [StickyATC] Cart operation started');
      this.setLoading(true);
    }
  }

  /**
   * Handle cart operation completed event
   */
  handleOperationCompleted(event) {
    if (event.detail.type === 'addToCart') {
      console.log('🏷️ [StickyATC] Cart operation completed');
      this.setLoading(false);
    }
  }

  /**
   * Handle cart operation failed event
   */
  handleOperationFailed(event) {
    if (event.detail.type === 'addToCart') {
      console.log('🏷️ [StickyATC] Cart operation failed');
      this.setLoading(false);
    }
  }

  /**
   * Set loading state
   */
  setLoading(isLoading) {
    if (!this.elements.button) return;

    if (isLoading) {
      this.elements.button.classList.add('is-loading');
      if (this.elements.loading) {
        this.elements.loading.style.display = '';
      }
      if (this.elements.buttonText) {
        this.elements.buttonText.style.opacity = '0.5';
      }
    } else {
      this.elements.button.classList.remove('is-loading');
      if (this.elements.loading) {
        this.elements.loading.style.display = 'none';
      }
      if (this.elements.buttonText) {
        this.elements.buttonText.style.opacity = '';
      }
    }
  }

  /**
   * Show the sticky ATC
   */
  show() {
    if (this.isVisible) return;

    this.isVisible = true;
    this.element.style.display = 'block';
    
    // Trigger reflow
    this.element.offsetHeight;
    
    this.element.classList.add('is-visible');

    this.dispatchEvent('sticky-atc:shown', {
      element: this.element,
      triggerPosition: this.triggerPosition
    });

    console.log('🏷️ [StickyATC] Shown');
  }

  /**
   * Hide the sticky ATC
   */
  hide() {
    if (!this.isVisible) return;

    this.isVisible = false;
    this.element.classList.remove('is-visible');

    // Wait for animation to complete before hiding
    setTimeout(() => {
      if (!this.isVisible) {
        this.element.style.display = 'none';
      }
    }, this.options.animationDuration);

    this.dispatchEvent('sticky-atc:hidden', {
      element: this.element,
      scrollPosition: window.scrollY
    });

    console.log('🏷️ [StickyATC] Hidden');
  }

  /**
   * Format money using Shopify's format
   */
  formatMoney(cents) {
    if (!this.options.formatMoney) {
      return `$${(cents / 100).toFixed(2)}`;
    }

    // Use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }

    // Fallback formatting
    return `$${(cents / 100).toFixed(2)}`;
  }

  /**
   * Dispatch custom event
   */
  dispatchEvent(eventName, detail) {
    this.element.dispatchEvent(new CustomEvent(eventName, {
      detail,
      bubbles: true
    }));
  }

  /**
   * Reset user close state (useful for testing or manual reset)
   */
  resetUserClose() {
    // Only reset if component is closeable
    if (!this.options.closeable) {
      console.warn('🏷️ [StickyATC] Reset close state attempted but component is not closeable');
      return;
    }

    this.isClosedByUser = false;
    
    if (this.options.rememberUserClose) {
      const storageKey = `sticky-atc-closed-${this.element.dataset.productId}`;
      localStorage.removeItem(storageKey);
    }

    // Recheck state
    this.checkInitialState();
  }

  /**
   * Force clear localStorage for this product (useful for debugging)
   */
  clearStorageForProduct() {
    const storageKey = `sticky-atc-closed-${this.element.dataset.productId}`;
    localStorage.removeItem(storageKey);
    this.isClosedByUser = false;
    
    console.log('🏷️ [StickyATC] Manually cleared localStorage for product', this.element.dataset.productId);
    
    // Recheck state
    this.checkInitialState();
  }

  /**
   * Update closeable state dynamically
   * @param {boolean} closeable - New closeable state
   */
  setCloseable(closeable) {
    const wasCloseable = this.options.closeable;
    this.options.closeable = closeable;
    
    // Update data attribute
    this.element.setAttribute('data-closeable', closeable.toString());
    
    // If changing from closeable to non-closeable, clear any close state
    if (wasCloseable && !closeable) {
      this.isClosedByUser = false;
      
      // Clear localStorage preference since it's no longer relevant
      if (this.options.rememberUserClose) {
        const storageKey = `sticky-atc-closed-${this.element.dataset.productId}`;
        localStorage.removeItem(storageKey);
      }
      
      // Show/hide close button
      if (this.elements.closeButton) {
        this.elements.closeButton.style.display = 'none';
      }
      
      console.log('🏷️ [StickyATC] Changed to non-closeable, cleared close state');
    } else if (!wasCloseable && closeable) {
      // Show close button if it exists
      if (this.elements.closeButton) {
        this.elements.closeButton.style.display = '';
      }
      
      console.log('🏷️ [StickyATC] Changed to closeable');
    }
    
    // Recheck state with new closeable setting
    this.checkInitialState();
  }

  /**
   * Destroy the component
   */
  destroy() {
    // Remove event listeners
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('variant:change', this.handleVariantChange);
    document.removeEventListener('variant:loaded', this.handleVariantLoaded);
    document.removeEventListener('variant:unavailable', this.handleVariantUnavailable);

    if (this.elements.closeButton) {
      this.elements.closeButton.removeEventListener('click', this.handleClose);
    }
    this.element.removeEventListener('click', this.handleStickyThumbnailClick);

    if (this.elements.button) {
      document.removeEventListener('cart:added', this.handleCartAdded);
      document.removeEventListener('cart:error', this.handleCartError);
      document.removeEventListener('operation:started', this.handleOperationStarted);
      document.removeEventListener('operation:completed', this.handleOperationCompleted);
      document.removeEventListener('operation:failed', this.handleOperationFailed);
    }

    // Hide if visible
    if (this.isVisible) {
      this.hide();
    }

    console.log('🏷️ [StickyATC] Destroyed');
  }
}

// Auto-initialize sticky ATC components
document.addEventListener('DOMContentLoaded', () => {
  const stickyATCs = document.querySelectorAll('[data-sticky-atc]');
  
  stickyATCs.forEach(element => {
    try {
      new StickyATC(element);
    } catch (error) {
      console.error('Failed to initialize StickyATC:', error);
    }
  });

  console.log(`🏷️ [StickyATC] Auto-initialized ${stickyATCs.length} sticky ATC components`);
});

// Handle Shopify section loading/unloading
document.addEventListener('shopify:section:load', (event) => {
  const stickyATC = event.target.querySelector('[data-sticky-atc]');
  if (stickyATC) {
    try {
      new StickyATC(stickyATC);
    } catch (error) {
      console.error('Failed to initialize StickyATC on section load:', error);
    }
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  const stickyATC = event.target.querySelector('[data-sticky-atc]');
  if (stickyATC && stickyATC.stickyATCInstance) {
    stickyATC.stickyATCInstance.destroy();
  }
});

// Global debugging utilities
window.StickyATCDebug = {
  /**
   * Clear all sticky ATC localStorage entries
   */
  clearAllStorage() {
    const keys = Object.keys(localStorage).filter(key => key.startsWith('sticky-atc-closed-'));
    keys.forEach(key => localStorage.removeItem(key));
    console.log(`🏷️ [StickyATC Debug] Cleared ${keys.length} localStorage entries:`, keys);
    
    // Refresh all sticky ATC components
    document.querySelectorAll('[data-sticky-atc]').forEach(element => {
      if (element.stickyATCInstance) {
        element.stickyATCInstance.checkInitialState();
      }
    });
  },

  /**
   * Show all sticky ATC localStorage entries
   */
  showStorage() {
    const entries = Object.keys(localStorage)
      .filter(key => key.startsWith('sticky-atc-closed-'))
      .map(key => ({ key, value: localStorage.getItem(key) }));
    
    console.log('🏷️ [StickyATC Debug] Current localStorage entries:', entries);
    return entries;
  },

  /**
   * Force show all sticky ATC components
   */
  forceShowAll() {
    document.querySelectorAll('[data-sticky-atc]').forEach(element => {
      if (element.stickyATCInstance) {
        element.stickyATCInstance.clearStorageForProduct();
      }
    });
  }
};

export default StickyATC;